/** A criterion a Remembered Search can lose to the page's search filters. */
export type FallbackCriterion = 'destination' | 'departure' | 'duration';

/**
 * A criterion a Remembered Search can lose on load: one the filters no longer
 * offer, or `'dates'` when remembered dates have gone stale.
 */
export type DroppedCriterion = FallbackCriterion | 'dates';

/** What the page's search filters currently offer. */
export interface OfferedCriteria {
  destination(id: number): boolean;
  departure(code: string): boolean;
  duration(value: number): boolean;
}

/**
 * Put back the authored value of every remembered criterion the page's search
 * filters no longer offer, before the component's defaults path reads the
 * `selected` inputs.
 *
 * Only controls a Remembered Search was applied to (those carrying a
 * `data-authored-*` value) are considered. A criterion only partly offered
 * falls back as a whole, so the visitor is never shown a silently narrowed
 * selection. The restored authored value then goes through the defaults path
 * as it always has — so one the filters don't offer either drops to the
 * component's own default.
 *
 * Hotels are not in the filters' destination tree, so they cannot be ruled
 * out; they fall back only with the destinations selected alongside them.
 *
 * Returns the criteria that fell back. Touches only the markup, never storage.
 */
export const fallBackUnavailableCriteria = (
  container: ParentNode,
  offered: OfferedCriteria
): FallbackCriterion[] => {
  const dropped: FallbackCriterion[] = [];

  const destination = container.querySelector<HTMLElement>(
    'button[data-search-destination]'
  );
  if (destination?.dataset.authoredDestinations !== undefined) {
    const ids = list(destination.dataset.selectedDestinations).map(Number);
    if (!ids.every(id => offered.destination(id))) {
      destination.dataset.selectedDestinations = destination.dataset.authoredDestinations;
      destination.dataset.selectedAccommodations =
        destination.dataset.authoredAccommodations ?? '';
      dropped.push('destination');
    }
  }

  const airport = container.querySelector<HTMLElement>('button[data-search-airport]');
  if (airport?.dataset.authoredAirports !== undefined) {
    const codes = list(airport.dataset.selectedAirports);
    if (!codes.every(code => offered.departure(code))) {
      airport.dataset.selectedAirports = airport.dataset.authoredAirports;
      dropped.push('departure');
    }
  }

  const duration = container.querySelector<HTMLElement>('button[data-search-duration]');
  if (duration?.dataset.authoredDuration !== undefined) {
    const value = duration.dataset.selectedDuration;
    if (value && !offered.duration(Number(value))) {
      duration.dataset.selectedDuration = duration.dataset.authoredDuration;
      dropped.push('duration');
    }
  }

  return dropped;
};

const list = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);
